import { useId, useState, type ReactNode } from 'react';
import { ArrowDown, ArrowUp, Plus, X } from 'lucide-react';

/**
 * The editing vocabulary Work's editors share. Every control states its error
 * next to itself, in words; nothing is signalled by colour alone.
 */

export function Field({
  label,
  hint,
  error,
  count,
  children,
  htmlFor,
}: {
  label: string;
  hint?: ReactNode;
  error?: string;
  /** [current, comfortable maximum]: shown as a counter that warns past the maximum. */
  count?: [number, number];
  children: ReactNode;
  htmlFor?: string;
}) {
  return (
    <div className="field">
      <div className="flex items-baseline justify-between gap-3">
        <label className="label" htmlFor={htmlFor}>
          {label}
        </label>
        {count && (
          <span className={`tnum text-xs ${count[0] > count[1] ? 'font-semibold text-warn' : 'text-fg-faint'}`}>
            {count[0]} / {count[1]}
          </span>
        )}
      </div>
      {children}
      {error ? (
        <p className="error-text" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="hint">{hint}</p>
      ) : null}
    </div>
  );
}

/** An on/off control that says what it controls; `role="switch"` for assistive tech. */
export function Switch({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!on)}
      className={`relative inline-flex h-6 w-10 shrink-0 items-center rounded-full border transition-colors duration-150 disabled:opacity-40 ${
        on ? 'border-ink bg-acid' : 'border-edge-strong bg-bg-sunk'
      }`}
    >
      <span
        className={`absolute size-4 rounded-full transition-transform duration-150 ${
          on ? 'translate-x-[1.125rem] bg-ink' : 'translate-x-[0.1875rem] bg-fg-faint'
        }`}
      />
    </button>
  );
}

/** Short tags (a tech stack): Enter or comma adds, × removes. */
export function ChipInput({
  value,
  onChange,
  placeholder,
  id,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  id?: string;
}) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim().replace(/,$/, '');
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft('');
  };
  return (
    <div className="input flex !min-h-11 flex-wrap items-center gap-1.5 !py-1.5">
      {value.map((v) => (
        <span key={v} className="chip !h-7 !text-xs !text-fg">
          {v}
          <button
            type="button"
            className="-mr-1 rounded-full p-0.5 hover:bg-edge"
            aria-label={`Remove ${v}`}
            onClick={() => onChange(value.filter((x) => x !== v))}
          >
            <X className="size-3" aria-hidden />
          </button>
        </span>
      ))}
      <input
        id={id}
        className="min-w-24 flex-1 bg-transparent py-1 outline-none"
        value={draft}
        placeholder={value.length ? '' : placeholder}
        onChange={(e) => (e.target.value.endsWith(',') ? (setDraft(e.target.value), add()) : setDraft(e.target.value))}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          }
          if (e.key === 'Backspace' && !draft && value.length) onChange(value.slice(0, -1));
        }}
        onBlur={add}
      />
    </div>
  );
}

function move<T>(list: T[], from: number, to: number) {
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

function RowTools({
  index,
  length,
  onMove,
  onRemove,
  noun,
}: {
  index: number;
  length: number;
  onMove: (to: number) => void;
  onRemove: () => void;
  noun: string;
}) {
  return (
    <div className="flex shrink-0 flex-col gap-0.5 pt-1">
      <button
        type="button"
        className="btn btn-ghost btn-icon !size-7 !min-h-7"
        disabled={index === 0}
        onClick={() => onMove(index - 1)}
        aria-label={`Move ${noun} ${index + 1} up`}
      >
        <ArrowUp className="size-3.5" aria-hidden />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-icon !size-7 !min-h-7"
        disabled={index === length - 1}
        onClick={() => onMove(index + 1)}
        aria-label={`Move ${noun} ${index + 1} down`}
      >
        <ArrowDown className="size-3.5" aria-hidden />
      </button>
      <button
        type="button"
        className="btn btn-ghost btn-icon !size-7 !min-h-7"
        onClick={onRemove}
        aria-label={`Remove ${noun} ${index + 1}`}
      >
        <X className="size-3.5" aria-hidden />
      </button>
    </div>
  );
}

/** An ordered list of paragraphs or lines. */
export function TextList({
  value,
  onChange,
  noun,
  rows = 3,
  errors,
  errorKey,
  placeholder,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  noun: string;
  rows?: number;
  errors?: Map<string, string>;
  errorKey: string;
  placeholder?: string;
}) {
  const base = useId();
  return (
    <div className="grid gap-3">
      {value.map((v, i) => (
        <div key={i} className="flex gap-2">
          <div className="grid flex-1 gap-1">
            <label htmlFor={`${base}-${i}`} className="sr-only">
              {noun} {i + 1}
            </label>
            <textarea
              id={`${base}-${i}`}
              className="input"
              rows={rows}
              value={v}
              placeholder={placeholder}
              aria-invalid={Boolean(errors?.get(`${errorKey}.${i}`))}
              onChange={(e) => onChange(value.map((x, j) => (j === i ? e.target.value : x)))}
            />
            {errors?.get(`${errorKey}.${i}`) && <p className="error-text">{errors.get(`${errorKey}.${i}`)}</p>}
          </div>
          <RowTools
            index={i}
            length={value.length}
            noun={noun}
            onMove={(to) => onChange(move(value, i, to))}
            onRemove={() => onChange(value.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <div>
        <button type="button" className="btn btn-quiet btn-sm" onClick={() => onChange([...value, ''])}>
          <Plus className="size-4" aria-hidden /> Add {noun}
        </button>
      </div>
    </div>
  );
}

/** An ordered list of two-part entries: a short head and a longer body. */
export function PairList<K extends string, V extends string>({
  value,
  onChange,
  keys,
  labels,
  noun,
  errors,
  errorKey,
}: {
  value: Record<K | V, string>[];
  onChange: (next: Record<K | V, string>[]) => void;
  keys: [K, V];
  labels: [string, string];
  noun: string;
  errors?: Map<string, string>;
  errorKey: string;
}) {
  const base = useId();
  const [k, v] = keys;
  return (
    <div className="grid gap-4">
      {value.map((row, i) => (
        <div key={i} className="flex gap-2">
          <div className="grid flex-1 gap-2 rounded-lg border border-edge bg-bg-raised p-3">
            <label htmlFor={`${base}-${i}-a`} className="sr-only">
              {labels[0]} {i + 1}
            </label>
            <input
              id={`${base}-${i}-a`}
              className="input font-semibold"
              placeholder={labels[0]}
              value={row[k]}
              aria-invalid={Boolean(errors?.get(`${errorKey}.${i}.${k}`))}
              onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))}
            />
            <label htmlFor={`${base}-${i}-b`} className="sr-only">
              {labels[1]} {i + 1}
            </label>
            <textarea
              id={`${base}-${i}-b`}
              className="input"
              rows={2}
              placeholder={labels[1]}
              value={row[v]}
              aria-invalid={Boolean(errors?.get(`${errorKey}.${i}.${v}`))}
              onChange={(e) => onChange(value.map((x, j) => (j === i ? { ...x, [v]: e.target.value } : x)))}
            />
            {(errors?.get(`${errorKey}.${i}.${k}`) || errors?.get(`${errorKey}.${i}.${v}`)) && (
              <p className="error-text">{errors?.get(`${errorKey}.${i}.${k}`) ?? errors?.get(`${errorKey}.${i}.${v}`)}</p>
            )}
          </div>
          <RowTools
            index={i}
            length={value.length}
            noun={noun}
            onMove={(to) => onChange(move(value, i, to))}
            onRemove={() => onChange(value.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <div>
        <button
          type="button"
          className="btn btn-quiet btn-sm"
          onClick={() => onChange([...value, { [k]: '', [v]: '' } as Record<K | V, string>])}
        >
          <Plus className="size-4" aria-hidden /> Add {noun}
        </button>
      </div>
    </div>
  );
}

/** A heading for one part of a long form. */
export function FormSection({ title, lede, children }: { title: string; lede?: ReactNode; children: ReactNode }) {
  return (
    <section className="grid gap-5 border-t border-edge pt-8 first:border-t-0 first:pt-0">
      <div>
        <h2 className="text-lg font-semibold">{title}</h2>
        {lede && <p className="mt-1 max-w-2xl text-sm text-fg-dim">{lede}</p>}
      </div>
      {children}
    </section>
  );
}
